import { useState, useEffect } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Lock, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getPaymentErrorMessage as getPaymentError } from "@/utils/userFriendlyErrors";

interface UpgradePaymentFormProps {
  userType: 'buyer' | 'seller';
  selectedPlan: 'monthly' | 'annual';
  onSuccess: () => void;
  onError: (error: string) => void;
  promoCode?: string;
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#ffffff",
      "::placeholder": {
        color: "#a0aec0",
      },
      backgroundColor: "transparent",
    },
    invalid: {
      color: "#ef4444",
    },
  },
};

interface SavedCard {
  id: string;
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

const UpgradePaymentForm = ({ 
  userType, 
  selectedPlan, 
  onSuccess, 
  onError,
  promoCode
}: UpgradePaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const { toast } = useToast();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [savedCard, setSavedCard] = useState<SavedCard | null>(null);
  const [useSavedCard, setUseSavedCard] = useState(true);
  const [cardholderName, setCardholderName] = useState("");
  const [billingAddress, setBillingAddress] = useState({
    line1: "",
    city: "",
    state: "",
    postal_code: "",
  });
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const fetchClientSecret = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          throw new Error("No active session");
        }

        const { data, error } = await supabase.functions.invoke(
          "setup-upgrade-payment",
          {
            body: { userType },
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          }
        );

        if (error) throw error;
        if (data?.clientSecret) {
          setClientSecret(data.clientSecret);
        }
        if (data?.savedCard) {
          setSavedCard(data.savedCard);
          setUseSavedCard(true);
        }
      } catch (err) {
        const errorMessage = "We couldn't set up the payment form. Please refresh the page and try again.";
        setError(errorMessage);
        onError(errorMessage);
      }
    };

    fetchClientSecret();
  }, [userType, onError]);

  const validateForm = () => {
    if (!cardholderName.trim()) {
      setError("Please enter the cardholder name");
      return false;
    }
    if (!billingAddress.line1.trim()) {
      setError("Please enter your billing address");
      return false;
    }
    if (!billingAddress.city.trim()) {
      setError("Please enter your city");
      return false;
    }
    if (!billingAddress.state.trim()) {
      setError("Please enter your state");
      return false;
    }
    if (!billingAddress.postal_code.trim()) {
      setError("Please enter your ZIP code");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('[UPGRADE-FORM] === PAYMENT FLOW STARTED ===');
    console.log('[UPGRADE-FORM] User Type:', userType);
    console.log('[UPGRADE-FORM] Selected Plan:', selectedPlan);
    console.log('[UPGRADE-FORM] Using Saved Card:', useSavedCard && !!savedCard);
    
    if (!stripe) {
      console.log('[UPGRADE-FORM] Stripe not initialized');
      return;
    }

    setIsProcessing(true);
    setError("");

    try {
      let paymentMethodId: string;
      let isExistingPaymentMethod = false;

      // If using saved card, use its payment method ID directly
      if (useSavedCard && savedCard) {
        paymentMethodId = savedCard.id;
        isExistingPaymentMethod = true;
        console.log('[UPGRADE-FORM] Using saved payment method:', paymentMethodId);
      } else {
        // New card flow - requires elements and validation
        if (!elements || !clientSecret) {
          console.log('[UPGRADE-FORM] Missing required dependencies for new card');
          return;
        }

        if (!validateForm()) {
          console.log('[UPGRADE-FORM] Form validation failed');
          return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
          throw new Error("Card element not found");
        }

        console.log('[UPGRADE-FORM] Step 1: Calling stripe.confirmCardSetup...');
        
        // Confirm card setup
        const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
          clientSecret,
          {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: cardholderName,
                address: {
                  line1: billingAddress.line1,
                  city: billingAddress.city,
                  state: billingAddress.state,
                  postal_code: billingAddress.postal_code,
                  country: "US",
                },
              },
            },
          }
        );

        console.log('[UPGRADE-FORM] Step 2: confirmCardSetup completed');

        if (stripeError) {
          console.error('[UPGRADE-FORM] Stripe error occurred:', stripeError);
          throw new Error(stripeError.message);
        }

        if (!setupIntent?.payment_method) {
          console.error('[UPGRADE-FORM] No payment method in setupIntent:', setupIntent);
          throw new Error("Payment method not created");
        }

        paymentMethodId = typeof setupIntent.payment_method === 'string' 
          ? setupIntent.payment_method 
          : setupIntent.payment_method.id;

        console.log('[UPGRADE-FORM] Step 3: New payment method created:', paymentMethodId);
      }

      // Complete upgrade on backend
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.error('[UPGRADE-FORM] No active session found');
        throw new Error("No active session");
      }

      console.log('[UPGRADE-FORM] Step 4: Session retrieved, calling complete-upgrade...');

      const { data, error: completeError } = await supabase.functions.invoke(
        "complete-upgrade",
        {
          body: {
            paymentMethodId,
            planType: selectedPlan,
            userType: userType,
            isExistingPaymentMethod,
            promoCode: promoCode || undefined,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      );

      console.log('[UPGRADE-FORM] Step 5: complete-upgrade response received');

      if (completeError) {
        console.error('[UPGRADE-FORM] complete-upgrade function error:', completeError);
        throw completeError;
      }
      
      if (!data?.success) {
        console.error('[UPGRADE-FORM] Upgrade not successful:', data);
        throw new Error(data?.error || "Failed to complete upgrade");
      }

      console.log('[UPGRADE-FORM] === PAYMENT FLOW SUCCESS ===');

      toast({
        title: "Upgrade Successful!",
        description: "Your subscription is now active. Redirecting...",
      });

      setTimeout(() => {
        onSuccess();
      }, 2000);

    } catch (err) {
      console.error('[UPGRADE-FORM] === PAYMENT FLOW ERROR ===');
      console.error('[UPGRADE-FORM] Error object:', err);
      
      if (err instanceof Error) {
        console.error('[UPGRADE-FORM] Error message:', err.message);
      }
      
      // Convert technical error to user-friendly message
      const technicalError = err instanceof Error ? err.message : "Payment failed";
      const userFriendlyMessage = getPaymentError(technicalError);
      setError(userFriendlyMessage);
      onError(userFriendlyMessage);
      toast({
        title: "Payment Issue",
        description: userFriendlyMessage,
        variant: "destructive",
      });
    } finally {
      console.log('[UPGRADE-FORM] Payment processing completed');
      setIsProcessing(false);
    }
  };

  const planDetails = userType === 'buyer' 
    ? {
        monthly: { price: '$9.99', period: 'per month' },
        annual: { price: '$99.99', period: 'per year' }
      }
    : {
        monthly: { price: '$34.99', oldPrice: '$68', period: 'per month' },
        annual: { price: '$349.99', oldPrice: '$680', period: 'per year' }
      };

  const getCardBrandIcon = (brand: string) => {
    const brandLower = brand.toLowerCase();
    if (brandLower === 'visa') return '💳';
    if (brandLower === 'mastercard') return '💳';
    if (brandLower === 'amex') return '💳';
    if (brandLower === 'discover') return '💳';
    return '💳';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Plan Summary */}
      <div className="bg-muted/20 rounded-lg p-4 border border-muted">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm text-muted-foreground">You'll pay</p>
            <p className="text-2xl font-bold text-godyspo-gold">
              {'oldPrice' in planDetails[selectedPlan] && (
                <span className="text-base font-medium text-muted-foreground line-through mr-2">{(planDetails[selectedPlan] as { oldPrice: string }).oldPrice}</span>
              )}
              {planDetails[selectedPlan].price}
            </p>
          </div>
          <p className="text-sm text-muted-foreground">{planDetails[selectedPlan].period}</p>
        </div>
      </div>

      {/* Saved Card Option */}
      {savedCard && (
        <div className="space-y-4">
          <Label>Payment Method</Label>
          
          {/* Use Saved Card Option */}
          <div 
            className={`p-4 border rounded-lg cursor-pointer transition-all ${
              useSavedCard 
                ? 'border-godyspo-gold bg-godyspo-gold/10' 
                : 'border-muted hover:border-muted-foreground/50'
            }`}
            onClick={() => setUseSavedCard(true)}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                useSavedCard ? 'border-godyspo-gold' : 'border-muted-foreground'
              }`}>
                {useSavedCard && <div className="w-3 h-3 rounded-full bg-godyspo-gold" />}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xl">{getCardBrandIcon(savedCard.brand)}</span>
                <div>
                  <p className="font-medium capitalize">
                    {savedCard.brand} •••• {savedCard.last4}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Expires {savedCard.exp_month}/{savedCard.exp_year}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Use Different Card Option */}
          <div 
            className={`p-4 border rounded-lg cursor-pointer transition-all ${
              !useSavedCard 
                ? 'border-godyspo-gold bg-godyspo-gold/10' 
                : 'border-muted hover:border-muted-foreground/50'
            }`}
            onClick={() => setUseSavedCard(false)}
          >
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                !useSavedCard ? 'border-godyspo-gold' : 'border-muted-foreground'
              }`}>
                {!useSavedCard && <div className="w-3 h-3 rounded-full bg-godyspo-gold" />}
              </div>
              <p className="font-medium">Use a different card</p>
            </div>
          </div>
        </div>
      )}

      {/* New Card Form - Only show if no saved card OR user chose different card */}
      {(!savedCard || !useSavedCard) && (
        <>
          {/* Cardholder Name */}
          <div className="space-y-2">
            <Label htmlFor="cardholderName">Cardholder Name</Label>
            <Input
              id="cardholderName"
              value={cardholderName}
              onChange={(e) => setCardholderName(e.target.value)}
              placeholder="John Doe"
              disabled={isProcessing}
            />
          </div>

          {/* Card Details */}
          <div className="space-y-2">
            <Label>Card Information</Label>
            <div className="p-3 border border-input rounded-md bg-background">
              <CardElement options={cardElementOptions} />
            </div>
          </div>

          {/* Billing Address */}
          <div className="space-y-4">
            <Label>Billing Address</Label>
            
            <Input
              value={billingAddress.line1}
              onChange={(e) => setBillingAddress({ ...billingAddress, line1: e.target.value })}
              placeholder="Street Address"
              disabled={isProcessing}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <Input
                value={billingAddress.city}
                onChange={(e) => setBillingAddress({ ...billingAddress, city: e.target.value })}
                placeholder="City"
                disabled={isProcessing}
              />
              <Input
                value={billingAddress.state}
                onChange={(e) => setBillingAddress({ ...billingAddress, state: e.target.value })}
                placeholder="State"
                maxLength={2}
                disabled={isProcessing}
              />
            </div>
            
            <Input
              value={billingAddress.postal_code}
              onChange={(e) => setBillingAddress({ ...billingAddress, postal_code: e.target.value })}
              placeholder="ZIP Code"
              disabled={isProcessing}
            />
          </div>
        </>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={!stripe || isProcessing || (!useSavedCard && !clientSecret)}
        className="w-full bg-godyspo-gold text-black hover:bg-godyspo-gold/90"
        size="lg"
      >
        {isProcessing ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Processing...
          </>
        ) : useSavedCard && savedCard ? (
          <>
            <Lock className="mr-2 h-4 w-4" />
            Complete Upgrade with {savedCard.brand} •••• {savedCard.last4}
          </>
        ) : (
          <>
            <Lock className="mr-2 h-4 w-4" />
            Complete Upgrade
          </>
        )}
      </Button>

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
        <Lock className="h-4 w-4" />
        <span>Secure payment powered by Stripe</span>
      </div>
    </form>
  );
};

export default UpgradePaymentForm;
